// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { Link } from '@/components/link';
import { NotFoundErrorBoundary } from '@/components/not-found-error-boundary';
import { SupportErrorBoundary } from '@/components/support-error-boundary';
import { VerticalNavigationLink } from '@/components/vertical-navigation-link';
import { TicketDetail } from '@/ticket/components/ticket-detail';
import { useTicket } from '@/ticket/hooks/use-ticket';
import { Box, Breadcrumbs, Container, Stack, Typography } from '@mui/material';
import { Outlet, createFileRoute } from '@tanstack/react-router';
import { Suspense } from 'react';

export const Route = createFileRoute('/_app/tickets/$ticketKey')({
  component: TicketDetailRoute,
});

function TicketDetailRoute() {
  const { ticketKey } = Route.useParams();

  return (
    <NotFoundErrorBoundary
      entityType="ticket"
      listPath="/tickets"
      listLabel="Go to tickets"
    >
      <TicketContent ticketKey={ticketKey} />
    </NotFoundErrorBoundary>
  );
}

function TicketContent(props: { ticketKey: string }) {
  const { ticket } = useTicket(props.ticketKey);

  return (
    <Stack>
      <Container maxWidth="xl">
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          paddingX={3}
          marginTop={3}
        >
          <Breadcrumbs separator="›" sx={{ py: 0.75 }}>
            <Link
              to="/tickets"
              variant="body2"
              color="text.secondary"
              sx={{ display: 'block' }}
            >
              Tickets
            </Link>
            <Typography variant="body2" color="text.secondary">
              {ticket.title}
            </Typography>
          </Breadcrumbs>
        </Stack>

        <TicketDetail ticket={ticket} />

        <Stack
          spacing={2}
          marginY={3}
          paddingX={{ xs: 1, md: 3 }}
          direction={{ xs: 'column', md: 'row' }}
        >
          <Stack
            direction={{ xs: 'row', md: 'column' }}
            borderTop="1px solid transparent"
          >
            <VerticalNavigationLink
              to="/tickets/$ticketKey"
              params={{ ticketKey: props.ticketKey }}
              activeOptions={{ exact: true }}
            >
              Notes ({ticket.noteCount})
            </VerticalNavigationLink>
            <VerticalNavigationLink
              to="/tickets/$ticketKey/attachments"
              params={{ ticketKey: props.ticketKey }}
            >
              Attachments ({ticket.fileCount})
            </VerticalNavigationLink>
            <VerticalNavigationLink
              to="/tickets/$ticketKey/audit"
              params={{ ticketKey: props.ticketKey }}
            >
              Audit log
            </VerticalNavigationLink>
          </Stack>
          <Box flexGrow={1} paddingLeft={{ xs: 0, md: 2 }}>
            <SupportErrorBoundary message="An error occurred.">
              <Suspense>
                <Outlet />
              </Suspense>
            </SupportErrorBoundary>
          </Box>
        </Stack>
      </Container>
    </Stack>
  );
}
